import { useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, SELECTION_CHANGE_COMMAND, COMMAND_PRIORITY_LOW } from 'lexical';

/**
 * Tracks the editor's text selection and reports a viewport rectangle for a
 * non-collapsed, MOUSE-made selection (Discord behaviour) via onSelectionRect.
 * ChatPanel owns the actual floating toolbar Popover (so the existing
 * selectionToolbar styling + document-mousedown dismissal are reused); this
 * plugin just feeds it { top, left } or null.
 *
 * The rect comes from the native DOM selection's bounding box — no mirror-div
 * measurement needed, a big simplification over the old textarea approach.
 */
export default function FloatingToolbarPlugin({ onSelectionRect }) {
  const [editor] = useLexicalComposerContext();
  const mouseDownRef = useRef(false);

  useEffect(() => {
    const root = editor.getRootElement();
    if (!root) return undefined;
    const onMouseDown = () => { mouseDownRef.current = true; };
    const onMouseUp = () => {
      // Read the selection on the next tick so the browser has finalized it.
      requestAnimationFrame(() => {
        report();
        mouseDownRef.current = false;
      });
    };
    root.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      root.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mouseup', onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // Compute + report the rect from the current selection, gated on a mouse drag.
  const report = () => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        onSelectionRect(null);
        return;
      }
      const dom = window.getSelection();
      if (!dom || dom.rangeCount === 0) {
        onSelectionRect(null);
        return;
      }
      const rect = dom.getRangeAt(0).getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        onSelectionRect(null);
        return;
      }
      onSelectionRect({ top: rect.top - 6, left: rect.left + rect.width / 2 });
    });
  };

  // Also clear the bar when the selection collapses via keyboard/programmatic.
  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        if (!mouseDownRef.current) {
          editor.getEditorState().read(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection) || selection.isCollapsed()) {
              onSelectionRect(null);
            }
          });
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  return null;
}
